// "Auch verwenden als ..." -- eine weitere Verwendung eines vorhandenen
// Eintrags (Anschrift, Kontaktweg) derselben Party, ohne ihn neu zu
// erfassen (ST-PTY-002; ADR-0056 Nachtrag Ziffer 3). Eine zurueckhaltende
// Zeilenaktion; die Zweckwahl erscheint erst auf Anforderung (5.2 Nr. 9) und
// bietet nur Zwecke an, die der Eintrag noch nicht hat.

import { useEffect, useId, useRef, type ReactElement } from 'react';
import { Button, Caption1, Field, makeStyles, Radio, RadioGroup, tokens } from '@fluentui/react-components';

import { usePflegeStyles } from '../shell/pflegeabschnitt.tsx';
import { NebenKnopf } from '../shell/nebenknopf.tsx';

const useStyles = makeStyles({
  // Eingerueckt unter die Beschriftung der Option, zu der er gehoert.
  optionshinweis: { display: 'block', paddingLeft: '2rem', marginBottom: '0.25rem', color: tokens.colorNeutralForeground2 },
});

/**
 * "Auch verwenden als ...": der Knopf, und erst nach ihm die Zweckwahl.
 *
 * Kein Zweck ist vorgewaehlt -- dieselbe Regel wie beim Hinzufuegen: eine
 * Vorbelegung machte aus einer vergessenen Entscheidung eine Aussage. Beim
 * Oeffnen steht der Fokus auf der ersten Option, beim Abbrechen wieder auf
 * dem Knopf (WCAG 2.4.3, dasselbe Muster wie das Beenden).
 */
export function WeitereVerwendung({
  name,
  frage,
  wirkung,
  optionen,
  offen,
  busy,
  wert,
  onWert,
  onOeffnen,
  onAbbrechen,
  onBestaetigen,
  zuruecktreten,
}: {
  /** Steht im Namen des Knopfes, etwa die Anschrift oder der Wert. */
  readonly name: string;
  /** Die Frage ueber der Zweckwahl. */
  readonly frage: string;
  /** Die Wirkung in einem Satz, vor der Entscheidung. */
  readonly wirkung: string;
  readonly optionen: readonly { readonly code: string; readonly label: string; readonly hint: string }[];
  readonly offen: boolean;
  readonly busy: boolean;
  /** Solange "Erneut versuchen" steht, ist das die eine Hauptaktion (NFR-UX-004). */
  readonly zuruecktreten: boolean;
  readonly wert: string;
  readonly onWert: (code: string) => void;
  readonly onOeffnen: () => void;
  readonly onAbbrechen: () => void;
  readonly onBestaetigen: () => void;
}): ReactElement {
  const pflege = usePflegeStyles();
  const eigen = useStyles();
  const wirkungId = useId();
  const hinweisId = useId();
  const panelRef = useRef<HTMLDivElement | null>(null);
  const knopfRef = useRef<HTMLDivElement | null>(null);
  const warOffen = useRef(false);
  const abgebrochen = useRef(false);
  useEffect(() => {
    if (offen) {
      panelRef.current?.querySelector<HTMLInputElement>('input[type="radio"]')?.focus();
    } else if (warOffen.current && abgebrochen.current) {
      knopfRef.current?.querySelector<HTMLButtonElement>('button')?.focus();
    }
    warOffen.current = offen;
    abgebrochen.current = false;
  }, [offen]);

  if (!offen) {
    return (
      <div className={pflege.zeilenaktionen} ref={knopfRef}>
        <Button
          appearance="secondary"
          size="small"
          className={pflege.zeilenknopf}
          disabled={busy}
          aria-label={`Auch verwenden als …: ${name}`}
          onClick={onOeffnen}
        >
          Auch verwenden als …
        </Button>
      </div>
    );
  }
  // Ein Zweck, den der Eintrag inzwischen hat (nach einem 409 und Neuladen),
  // steht nicht mehr zur Wahl und zaehlt nicht als Auswahl.
  const gewaehlt = optionen.find((eintrag) => eintrag.code === wert);
  return (
    <div className={pflege.beenden} ref={panelRef}>
      <Field label={frage} required>
        <RadioGroup aria-describedby={wirkungId} value={gewaehlt?.code ?? ''} onChange={(_event, data) => onWert(data.value)}>
          {/* Der Hinweis steht UNTER jeder Option und beschreibt sie: der
              Unterschied etwa zwischen "geschaeftlich" und "Assistenz" soll
              vor der Wahl lesbar und hoerbar sein, nicht erst danach. */}
          {optionen.map((eintrag) => (
            <div key={eintrag.code}>
              <Radio value={eintrag.code} label={eintrag.label} aria-describedby={`${hinweisId}-${eintrag.code}`} />
              <Caption1 id={`${hinweisId}-${eintrag.code}`} className={eigen.optionshinweis}>
                {eintrag.hint}
              </Caption1>
            </div>
          ))}
        </RadioGroup>
      </Field>
      <Caption1 id={wirkungId}>{wirkung}</Caption1>
      <div className={pflege.zeilenaktionen}>
        <Button appearance={zuruecktreten ? 'secondary' : 'primary'} disabled={busy || gewaehlt === undefined} onClick={onBestaetigen}>
          Zweck hinzufügen
        </Button>
        <NebenKnopf
          disabled={busy}
          onClick={() => {
            abgebrochen.current = true;
            onAbbrechen();
          }}
        >
          Abbrechen
        </NebenKnopf>
      </div>
    </div>
  );
}
